import { List, Typography } from "@mui/material";

import { Grid, GridColumn, GridRowClickEvent } from "@progress/kendo-react-grid";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ViewToggleButton } from "../../components/commons/ViewToggleButton";
import { Banner } from "../../constante/banners";
import { planogramme } from "../../constante/routes";
import { UrlParams } from "../../constante/UrlParams";
import { ViewMode } from "../../constante/viewMode";
import { Dashboard } from "../../models/Dashboard";
import { Division } from "../../models/Division";
import { Magasin } from "../../models/Magasin";
import { SecMagasin } from "../../models/SecMagasin";
import { Section } from "../../models/Section";
import { planoAPI } from "../../redux/features/cache";
import { useAppSelector } from "../../redux/store";
import { DashboardCard } from "./DashboardCard";
import { ItemRenderer } from "./ItemRenderer";

type QuantityData = {
  floorQuantity: number;
  targetQuantity: number;
};
type SectionData = { section: string } & QuantityData;
type StoreData = { store: string } & QuantityData;

type DashboardData = StoreData | SectionData;

export const DashboardPage = () => {
  const navigate = useNavigate();

  const mode = useAppSelector((state) => state.appState.buttonsState);

  const [viewMode, setViewMode] = useState<ViewMode>();

  const [selectedStore, setSelectedStore] = useState<Magasin | null>(null);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);

  const { data: stores, } = planoAPI.endpoints.getMagasins.useQuery({});
  const [processedStores, setProcessedStores] = useState<Magasin[] | undefined>()

  const [fetchSections, { data: sections }] = planoAPI.endpoints.getSections.useLazyQuery();
  const [fetchDivisions, { data: divisions }] = planoAPI.endpoints.getDivisions.useLazyQuery();
  const [fetchDashboard, { data: dashboards, isSuccess: isSuccessDashboards }] = planoAPI.endpoints.getDashboard.useLazyQuery();

  const [dashboardData, setDashboardData] = useState<
    DashboardData[] | undefined
  >();
  const [showGrid, setShowGrid] = useState<boolean>(false);

  const reset = useCallback(() => {
    setSelectedStore(() => null);
    setSelectedSection(() => null);
    setSelectedDivision(() => null);
    setDashboardData(() => []);
    setShowGrid(false);
  }, []);

  useEffect(() => {
    if (stores) {
      const filteredStores = stores.filter(store => store.maggrp_cd !== Banner.Warehouse);
      setProcessedStores(filteredStores);
    }
  }, [stores]);

  useEffect(() => {
    switch (viewMode) {
      case ViewMode.product:
        setShowGrid(!!selectedDivision && !!selectedSection);
        break;
      case ViewMode.store:
        setShowGrid(!!selectedStore && !!selectedDivision);
        break;
      default:
        setShowGrid(false);
        break;
    }
  }, [selectedDivision, selectedSection, selectedStore, viewMode]);

  useEffect(() => {
    const nextMode = mode.productView ? ViewMode.product : ViewMode.store;

    setViewMode(() => nextMode);

    reset();

    switch (nextMode) {
      case ViewMode.product: {
        fetchDivisions({
          treeView: true,
          includeSections: false,
          plano: true,
          user: "",
        });
        break;
      }
      case ViewMode.store: {
        break;
      }
      default:
        break;
    }
  }, [fetchDivisions, mode, mode.productView, mode.storeView, reset]);

  useEffect(() => {
    if (isSuccessDashboards && dashboards) {
      let data: DashboardData[] = [];

      switch (viewMode) {
        case ViewMode.product: {
          const storeDashboard = dashboards.at(0);
          if (storeDashboard) {
            data = storeDashboard?.sectionStores.map(
              (section: SecMagasin) =>
              ({
                store: section.magasin.mag_nom_aff,
                floorQuantity: section.nombre_reel,
                targetQuantity: section.nombre_vise,
              } as DashboardData)
            );

            setDashboardData(data);
          }
          break;
        }
        case ViewMode.store: {
          data = dashboards.map((dashboard: Dashboard) => (
            {
              section: dashboard.sectionName,
              floorQuantity: dashboard.sectionStores.find((store) => store.mag_num === selectedStore?.mag_num)?.nombre_reel,
              targetQuantity: dashboard.sectionStores.find((store) => store.mag_num === selectedStore?.mag_num)?.nombre_vise,
            } as DashboardData
          ));
          setDashboardData(data);
          break;
        }
        default:
          break;
      }
      setDashboardData(data);
    }
  }, [dashboards, isSuccessDashboards, selectedStore?.mag_num, viewMode]);

  const handleSelectedSection = useCallback((section: Section) => {
    setSelectedSection(section);

    switch (viewMode) {
      case ViewMode.store:
        break;
      case ViewMode.product:
      default: {
        fetchDashboard({
          section: section.plans_seq.toString(),
          div: section?.div_num.toString(),
          detail: false,
        });
        break;
      }
    }
  },
    [fetchDashboard, viewMode]
  );

  const handleSelectedDivision = useCallback((division: Division) => {
    setSelectedDivision(division);

    fetchSections({
      div: division.div_num.toString(),
      rech: "",
      simple: true,
      treeView: false,
      user: "",
    });

    switch (viewMode) {
      case ViewMode.store: {

        fetchDashboard({
          div: division.div_num.toString(),
          detail: false,
          mag: selectedStore?.mag_num.toString(),
        });
        break;
      }
      case ViewMode.product: {
        setSelectedSection(null);
        setDashboardData(undefined);
        break;
      }
      default:
        break;
    }
  },
    [fetchDashboard, fetchSections, selectedStore?.mag_num, viewMode]
  );

  const handleSelectedStore = useCallback((store: Magasin) => {
    setSelectedStore(store);
    switch (viewMode) {
      case ViewMode.store: {
        setSelectedDivision(null);
        setDashboardData(undefined);
        fetchDivisions({
          treeView: true,
          includeSections: false,
          plano: true,
          user: "",
        });
        break;
      }
      case ViewMode.product:
      default:
        break;
    }
  }, [fetchDivisions, viewMode]);

  const divisionCard = useMemo(() => (
    <DashboardCard title={"Divisions"}>
      <List disablePadding>
        {divisions && divisions.map(division => (
          <ItemRenderer
            selected={division.div_num === selectedDivision?.div_num}
            item={division}
            text={division.div_nom_aff}
            handleSelectedItem={handleSelectedDivision}
          />
        ))}
      </List>
    </DashboardCard>
  ),
    [divisions, handleSelectedDivision, selectedDivision?.div_num]
  );

  const sectionCard = useMemo(() => (
    <DashboardCard title="Sections">
      <List disablePadding>
        {sections && sections.map(section => (
          <ItemRenderer
            selected={section.plans_seq === selectedSection?.plans_seq}
            item={section}
            handleSelectedItem={handleSelectedSection}
            text={section.plans_nom}
          />
        ))}
      </List>
    </DashboardCard>
  ), [handleSelectedSection, sections, selectedSection?.plans_seq]);

  const storeCard = useMemo(() => {
    return (
      <DashboardCard title="Magasins">
        <List disablePadding>
          {processedStores && processedStores.map(store => (
            <ItemRenderer
              selected={store.mag_num === selectedStore?.mag_num}
              item={store}
              handleSelectedItem={handleSelectedStore}
              text={store.mag_nom_aff}
            />
          ))}
        </List>
      </DashboardCard>
    );
  }, [handleSelectedStore, selectedStore?.mag_num, processedStores]);

  useEffect(() => {
    if (!!selectedDivision && !!selectedSection && !!selectedStore) {
      const searchParams = new URLSearchParams();

      searchParams.set(UrlParams.Section, selectedSection.plans_seq.toString());
      searchParams.set(UrlParams.Division, selectedDivision.div_num.toString());
      searchParams.set(UrlParams.Magasin, selectedStore.mag_num.toString());
      searchParams.set(UrlParams.Statut, "SortirPL,SurPlancher,Vendu");

      navigate(`${planogramme}?${searchParams.toString()}`);
    }
  }, [navigate, selectedDivision, selectedSection, selectedStore]);

  const handleOnClick = useCallback((event: GridRowClickEvent) => {
    event.syntheticEvent.preventDefault();

    switch (viewMode) {
      case ViewMode.product: {
        const storeRow = event.dataItem as StoreData;
        const store = processedStores?.find(store => store.mag_nom_aff === storeRow.store);
        setSelectedStore(() => store ?? null);
        break;
      }
      case ViewMode.store: {
        const sectionRow = event.dataItem as SectionData;
        const section = sections?.find(section => section.plans_nom.toString() === sectionRow.section);
        setSelectedSection(() => section ?? null);
        break;
      }
      default:
        break;
    }
  }, [processedStores, sections, viewMode]);

  const resultGrid = useMemo(() => {
    let field: string, title: string;
    switch (viewMode) {
      case ViewMode.product:
        field = "store";
        title = "Magasins";
        break;
      case ViewMode.store:
      default:
        field = "section";
        title = "Sections";
        break;
    }

    return (
      <DashboardCard title="Résultats">
        <Grid
          data={dashboardData}
          onRowClick={handleOnClick}
        >
          <GridColumn title={title} field={field} resizable />
          <GridColumn
            title="Qté sur le plancher"
            field="floorQuantity"
            headerClassName="alignRight"
            className="alignRight"
            width={200}
          />
          <GridColumn
            title="Qté visée"
            field="targetQuantity"
            headerClassName="alignRight"
            className="alignRight"
            width={200}
          />
        </Grid>
      </DashboardCard>
    );
  }, [dashboardData, handleOnClick, viewMode]);

  return (
    <div
      id="dashboard"
      style={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "1600px",
        margin: "0 auto",
      }}
    >
      <Typography variant="h3">Tableau de bord</Typography>
      <Typography variant="overline">Afficher par</Typography>
      <div style={{ display: "flex" }}>
        <ViewToggleButton />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 4fr",
        }}
      >
        {mode.productView ? divisionCard : storeCard}
        {mode.productView
          ? selectedDivision
            ? sectionCard
            : undefined
          : selectedStore
            ? divisionCard
            : undefined}
        {showGrid && dashboardData ? resultGrid : undefined}
      </div>
    </div>
  );
};
